import { useState, useEffect, useCallback, useRef } from "react";
import {
    Box,
    Typography,
    AppBar,
    Toolbar,
    IconButton,
    Menu,
    MenuItem,
    Card,
    CardContent,
    Chip,
    Alert,
    Slide,
    Fade,
    CircularProgress,
    Button,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"; // ✅ to get queueNumber
import ScrollButtons from "../../../components/ScrollButtons";
import Logo from "../../../assests/logo.png";
// Demo images for portrait ads
import DemoPortrait1 from "../../../assests/Potrait/1.png";
import DemoPortrait2 from "../../../assests/Potrait/2.png";
import DemoPortrait3 from "../../../assests/Potrait/3.jpg";
import DemoPortrait4 from "../../../assests/Potrait/4.jpg";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import MenuIcon from "@mui/icons-material/Menu";
import QueueManager from "../../../utils/QueueManager";
import { getActiveAds, checkQueueStatus as fetchQueueStatus, getAllServices, getServiceSettings, getMenus, getBranchDisplayInfo } from "../../../api/api";
import { getUserFrontendUrl, getStorageUrl } from "../../../config/config";
import BranchHeader from "./BranchHeader";
import useQueueUpdates from "../../../hooks/useQueueUpdates";
import { trackEvent } from "../../../utils/analytics";
import Cookies from "js-cookie";

// Demo ads for demo mode (defined outside component to avoid recreation)
const demoAds = [
    {
        id: "demo-1",
        title: "Demo Ad 1",
        image_url: DemoPortrait1,
        description: "Sample portrait advertisement",
    },
    {
        id: "demo-2",
        title: "Demo Ad 2",
        image_url: DemoPortrait2,
        description: "Sample portrait advertisement",
    },
    {
        id: "demo-3",
        title: "Demo Ad 3",
        image_url: DemoPortrait3,
        description: "Sample portrait advertisement",
    },
    {
        id: "demo-4",
        title: "Demo Ad 4",
        image_url: DemoPortrait4,
        description: "Sample portrait advertisement",
    },
];

const QueueDisplay = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Get data from URL parameters (for SMS tracking)
    const urlQueueId = searchParams.get("queue_id");
    const urlQueueNumber = searchParams.get("queue_number");
    const urlBranchId = searchParams.get("branch_id");
    const urlServiceIds = searchParams.get("service_ids");

    // Get data from location state (for QR flow)
    const {
        queueNumber: stateQueueNumber = "00",
        selectedOptions: stateSelectedOptions = [],
        ticketCode: stateTicketCode = "DEMO",
        isDemoMode: stateIsDemoMode = true,
        existing: stateExisting = false,
    } = location.state || {};

    // Use URL parameters if available (SMS flow), otherwise use state (QR flow)
    const queueNumber = urlQueueNumber || stateQueueNumber;
    const isSmsFlow = !!urlQueueId;

    // Convert service IDs to selected options for SMS flow
    const [smsSelectedOptions, setSmsSelectedOptions] = useState([]);

    // Load services for SMS flow
    useEffect(() => {
        const loadServicesForSms = async () => {
            if (isSmsFlow && urlServiceIds && urlBranchId) {
                try {
                    const services = await getAllServices(urlBranchId);

                    // Convert service IDs to service objects
                    const serviceIdArray = urlServiceIds
                        .split(",")
                        .filter((id) => id.trim() !== "");
                    const selectedServices = services.filter((service) =>
                        serviceIdArray.includes(service.id.toString())
                    );

                    console.log(
                        "SMS: Loaded services for display:",
                        selectedServices
                    );
                    setSmsSelectedOptions(selectedServices);
                } catch (error) {
                    console.error("SMS: Failed to load services:", error);
                }
            }
        };

        loadServicesForSms();
    }, [isSmsFlow, urlServiceIds, urlBranchId]);

    // Use SMS selected options if available, otherwise use state selected options
    const selectedOptions = isSmsFlow
        ? smsSelectedOptions
        : stateSelectedOptions;

    // For SMS flow, use live mode (not demo)
    // Check if ticket code is DEMO to determine demo mode (more reliable than state)
    const isDemoModeFromTicket = stateTicketCode === "DEMO";
    const isDemoMode = isSmsFlow ? false : (stateIsDemoMode !== undefined ? stateIsDemoMode : isDemoModeFromTicket);
    const ticketCode = isSmsFlow ? "SMS" : stateTicketCode;
    const existing = isSmsFlow ? true : stateExisting || false;

    // Log the received data for debugging
    console.log("NextCustomerView received selectedOptions:", selectedOptions);
    console.log("Flow type:", isSmsFlow ? "SMS" : "QR");
    console.log("Mode:", isDemoMode ? "DEMO" : "LIVE");
    console.log("Ticket code:", ticketCode);
    console.log("Existing:", existing);
    console.log("URL parameters:", {
        urlQueueId,
        urlQueueNumber,
        urlBranchId,
        urlServiceIds,
    });
    console.log("State data:", {
        stateQueueNumber,
        stateSelectedOptions,
        stateTicketCode,
        stateIsDemoMode,
        stateExisting,
    });

    // Every customer-facing text and the logo on this page belong to one branch.
    // The SMS link carries it in the URL; the QR flow leaves it in state or,
    // on a staff device, in localStorage.
    const branchId =
        urlBranchId ||
        stateExisting?.branch_id ||
        localStorage.getItem("branch_id");

    const [currentIndex, setCurrentIndex] = useState(0);
    const [queueNumberLabel, setQueueNumberLabel] = useState("Ditt kønummer er");
    const [beingServedMessage, setBeingServedMessage] = useState("Du blir nå betjent!");
    const [selectedServicesLabel, setSelectedServicesLabel] = useState("Your selected services:");
    const [waitingTimeMessage, setWaitingTimeMessage] = useState("");
    const [serviceRecommendationText, setServiceRecommendationText] = useState("");
    const [branchName, setBranchName] = useState("");
    const [branchLogoUrl, setBranchLogoUrl] = useState(null);
    const [menuItems, setMenuItems] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [isBeingServed, setIsBeingServed] = useState(false);
    const [ads, setAds] = useState([]);
    const [queueStatus, setQueueStatus] = useState(null);
    const [isLoadingQueue, setIsLoadingQueue] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const open = Boolean(anchorEl);

    // Fix scrolling issue by overriding global overflow hidden
    useEffect(() => {
        const rootElement = document.getElementById("root");
        const htmlElement = document.documentElement;

        document.body.style.overflow = "auto";
        document.body.style.height = "auto";
        htmlElement.style.overflow = "auto";
        htmlElement.style.height = "auto";

        if (rootElement) {
            rootElement.style.overflow = "auto";
            rootElement.style.height = "auto";
        }

        document.body.classList.add("scrollable-page");
        htmlElement.classList.add("scrollable-page");

        return () => {
            document.body.style.overflow = "hidden";
            document.body.style.height = "100%";
            htmlElement.style.overflow = "hidden";
            htmlElement.style.height = "100%";
            if (rootElement) {
                rootElement.style.overflow = "hidden";
                rootElement.style.height = "100%";
            }
            document.body.classList.remove("scrollable-page");
            htmlElement.classList.remove("scrollable-page");
        };
    }, []);

    // Load the customer-facing texts from service settings
    useEffect(() => {
        if (!branchId) return;
        const fetchSettings = async () => {
            try {
                const response = await getServiceSettings(branchId);
                if (response.success && response.data) {
                    if (response.data.queue_number_label) {
                        setQueueNumberLabel(response.data.queue_number_label);
                    }
                    if (response.data.being_served_message) {
                        setBeingServedMessage(response.data.being_served_message);
                    }
                    if (response.data.selected_services_label) {
                        setSelectedServicesLabel(response.data.selected_services_label);
                    }
                    setWaitingTimeMessage(response.data.waiting_time_message || "");
                    setServiceRecommendationText(
                        response.data.service_recommendation_text || ""
                    );
                }
            } catch (err) {
                console.error("Error fetching queue number label:", err);
            }
        };
        fetchSettings();
    }, [branchId]);

    // Branch branding. The customer opening the SMS link has never logged in
    // here, so the logo and name have to come from the branch id in the link -
    // anything else shows them another shop's brand.
    useEffect(() => {
        if (!branchId) return;
        let cancelled = false;
        const fetchBranding = async () => {
            const data = await getBranchDisplayInfo(branchId);
            if (cancelled || !data) return;
            setBranchName(data.branch_name || "");
            setBranchLogoUrl(
                data.logo ? getStorageUrl(`/storage/logos/${data.logo}`) : null
            );
        };
        fetchBranding();
        return () => {
            cancelled = true;
        };
    }, [branchId]);

    // Fetch menu items when branch id is available
    useEffect(() => {
        if (!branchId) return;
        const fetchMenuItems = async () => {
            try {
                const response = await getMenus(branchId);
                if (response.success) {
                    setMenuItems(response.data || []);
                }
            } catch (error) {
                console.error("Error fetching menu items:", error);
                setMenuItems([]);
            }
        };
        fetchMenuItems();
    }, [branchId]);

    // Initialize demo ads immediately if in demo mode
    useEffect(() => {
        if (isDemoMode && ads.length === 0) {
            console.log("Demo mode detected - initializing demo ads immediately");
            setAds(demoAds);
            setLoading(false);
        }
    }, [isDemoMode, ads.length]);

    // Fetch portrait ads from database
    const fetchPortraitAds = async () => {
        // Skip API call if in demo mode - use demo ads directly
        if (isDemoMode) {
            console.log("Demo mode: Using demo ads (skipping API call)");
            setAds(demoAds);
            setLoading(false);
            setError(null);
            return;
        }

        try {
            setLoading(true);
            // Without an explicit branch this falls back to localStorage and
            // then to branch 1, so the customer's phone showed another
            // client's ads.
            const portraitAds = await getActiveAds("portrait", null, branchId);

            if (portraitAds.success && portraitAds.data.length > 0) {
                setAds(portraitAds.data);
            } else {
                setAds([]);
            }
            setError(null);
        } catch (err) {
            console.error("Error fetching portrait ads:", err);
            setError("Failed to load ads");
            setAds([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPortraitAds();
    }, [isDemoMode, branchId]);

    useEffect(() => {
        if (ads.length > 0) {
            const interval = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
            }, 5000); // Change image every 5 seconds
            return () => clearInterval(interval);
        }
    }, [ads]);

    // An ad counts once per page view however often the rotation comes back to
    // it - this page changes image every 5s and a customer can sit on it for
    // twenty minutes, which would otherwise be hundreds of duplicate events.
    const seenAdIds = useRef(new Set());

    useEffect(() => {
        if (isDemoMode) return;

        const ad = ads[currentIndex];
        if (!ad || seenAdIds.current.has(ad.id)) return;

        seenAdIds.current.add(ad.id);
        trackEvent("ad_impression", {
            ad_id: ad.id,
            ad_title: ad.title,
            ad_type: ad.image_type,
            placement: "sms_tracking_page",
        });
    }, [ads, currentIndex, isDemoMode]);

    const handleAdClick = (ad) => {
        if (!ad?.target_url) return;

        trackEvent("ad_click", {
            ad_id: ad.id,
            ad_title: ad.title,
            ad_type: ad.image_type,
            placement: "sms_tracking_page",
        });

        window.open(ad.target_url, "_blank", "noopener,noreferrer");
    };

    // Everything the status handlers need, kept in a ref so a changing service
    // list or navigate identity never tears down the poll or the subscription.
    const latest = useRef(null);
    // Only the very first status read shows the loading state; later refreshes
    // happen underneath the customer without blanking the page.
    const firstStatusFetch = useRef(true);
    latest.current = {
        queueNumber,
        selectedOptions,
        ticketCode,
        isDemoMode,
        isSmsFlow,
        urlBranchId,
        branchId,
        navigate,
    };

    const goToThankYou = useCallback(() => {
        const {
            queueNumber: nr,
            selectedOptions: services,
            ticketCode: code,
            isDemoMode: demo,
            isSmsFlow: sms,
            urlBranchId: branch,
            navigate: go,
        } = latest.current;

        QueueManager.clearQueue(code);

        // In the SMS flow ticketCode is the literal "SMS", so the branch id
        // from the URL is the only usable identifier - pass it through or the
        // thank-you page loses all branding.
        let ticketData = null;
        const ticketDetails = Cookies.get("ticketDetails");
        if (ticketDetails) {
            try {
                ticketData = JSON.parse(ticketDetails);
            } catch (e) {
                console.error("Error parsing ticketDetails:", e);
            }
        }
        if (!ticketData && branch) {
            ticketData = {
                client: { id: parseInt(branch) },
                ticket_code: code,
            };
        }

        go("/home/thank-you", {
            state: {
                queueNumber: nr,
                selectedOptions: services,
                ticketCode: code,
                isDemoMode: demo,
                existing: true,
                isSmsFlow: sms,
                ticketData,
            },
        });
    }, []);

    // Single place that turns a queue status into what the customer sees, so
    // the Pusher event and the poll can never disagree.
    const applyQueueStatus = useCallback(
        (status) => {
            if (!status) return;
            setQueueStatus(status);
            if (status === "ASSISTED") {
                goToThankYou();
            } else {
                setIsBeingServed(status === "SERVING");
            }
        },
        [goToThankYou]
    );

    // Read the current status from the public endpoint. /queue/{id} needs an
    // admin token and the SMS customer has none, so /queue-status is the only
    // one this page can call.
    const refreshQueueStatus = useCallback(async () => {
        if (document.hidden) return;

        const { queueNumber: nr, branchId: branch, isDemoMode: demo } =
            latest.current;
        if (demo || !branch || !nr || nr === "00") return;

        if (firstStatusFetch.current) setIsLoadingQueue(true);
        try {
            const response = await fetchQueueStatus(nr, branch);
            applyQueueStatus(response?.queue?.QueueStatus);
        } catch (err) {
            console.error("Failed to check queue status", err);
        } finally {
            if (firstStatusFetch.current) {
                firstStatusFetch.current = false;
                setIsLoadingQueue(false);
            }
        }
    }, [applyQueueStatus]);

    const trackingEnabled =
        !isDemoMode &&
        Boolean(branchId) &&
        Boolean(queueNumber) &&
        queueNumber !== "00";

    // One read on load, so a refresh always shows the current state.
    useEffect(() => {
        if (trackingEnabled) refreshQueueStatus();
    }, [trackingEnabled, refreshQueueStatus]);

    // Live updates through the shared hook rather than a bare subscription of
    // this page's own. It unwraps the broadcast payload correctly - Laravel
    // nests the event's public $data, so the queue arrives at data.data.queue
    // and the `data.queue` this page used to read was always undefined, which
    // silently dropped every Assist and Done that reached it. The hook also
    // brings the heartbeat, so a socket that connects but never delivers can
    // no longer leave the page frozen: the old poll was gated on the
    // connection state alone and went quiet the moment Pusher said "connected".
    useQueueUpdates({
        branchId,
        enabled: trackingEnabled,
        onRefresh: refreshQueueStatus,
        onQueueEvent: (queueData) => {
            const { queueNumber: nr, isSmsFlow: sms } = latest.current;
            const isOurQueue =
                queueData.QueueNr == nr ||
                (sms && queueData.QueueID == parseInt(urlQueueId, 10));
            if (!isOurQueue) return;

            console.log(
                "📨 NextCustomerView: our queue changed to",
                queueData.QueueStatus
            );
            applyQueueStatus(queueData.QueueStatus);
        },
    });

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 2,
                            }}
                        >
                            <img
                                src={Logo}
                                alt="Logo"
                                style={{
                                    height: "32px",
                                    marginRight: "16px",
                                    filter: "brightness(0) invert(1)", // Make logo white
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ flexGrow: 1 }}
                        >
                            <span
                                style={{
                                    color: isDemoMode ? "#1976d2" : "#2e7d32",
                                    fontWeight: "bold",
                                }}
                            >
                                {isDemoMode ? "DEMO MODE" : ""}
                            </span>
                        </Typography>

                        <IconButton
                            size="large"
                            color="inherit"
                            aria-label="menu"
                            sx={{ ml: 2 }}
                            onClick={handleClick}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Top menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {/* Default HOME menu item */}
                            <MenuItem onClick={handleClose}>
                                <a
                                    href={getUserFrontendUrl(`/get-ticket/${ticketCode || "DEMO"}`)}
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    HOME
                                </a>
                            </MenuItem>
                            {/* Dynamic menu items from database - only show if admin has set a real URL (not placeholder) */}
                            {menuItems
                                .filter((menu) => {
                                    if (!menu.menu_link || (!menu.menu_link.startsWith('http://') && !menu.menu_link.startsWith('https://'))) return false;
                                    const placeholders = [
                                        'https://www.facebook.com/yourpage',
                                        'https://www.instagram.com/yourprofile',
                                        'https://www.example.com/contact',
                                    ];
                                    return !placeholders.includes(menu.menu_link.trim().toLowerCase());
                                })
                                .map((menu) => (
                                    <MenuItem key={menu.id} onClick={() => {
                                        handleClose();
                                        trackEvent("menu_click", {
                                            menu_title: menu.menu_title,
                                            placement: "sms_tracking_page",
                                        });
                                        window.open(menu.menu_link, '_blank', 'noopener,noreferrer');
                                    }}>
                                        {menu.menu_title}
                                    </MenuItem>
                                ))}
                        </Menu>
                    </Toolbar>
                </AppBar>
            </Box>

            {/* Main content */}
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={4}
            >
                {/* Branch branding - logo when the branch uploaded one, name otherwise */}
                <BranchHeader
                    logoUrl={branchLogoUrl}
                    branchName={branchName}
                    isMobile={isMobile}
                />

                {/* Loading indicator for queue status check */}
                {isLoadingQueue && (
                    <Box display="flex" alignItems="center" gap={2} mt={2}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                            Checking queue status...
                        </Typography>
                    </Box>
                )}

                <Typography variant="h6" fontWeight="bold" mt={2}>
                    {queueNumberLabel || "Ditt kønummer er"}
                </Typography>

                {/* ✅ Real queue number */}
                <Typography variant="h1" fontWeight="bold">
                    {queueNumber}
                </Typography>

                {/* Serving Status Banner */}
                <Slide
                    direction="down"
                    in={isBeingServed}
                    mountOnEnter
                    unmountOnExit
                >
                    <Alert
                        severity="success"
                        sx={{
                            mt: 2,
                            mb: 2,
                            fontSize: "1.2rem",
                            fontWeight: "bold",
                            borderRadius: 2,
                            boxShadow: 3,
                            animation: "pulse 2s infinite",
                            "@keyframes pulse": {
                                "0%": { transform: "scale(1)" },
                                "50%": { transform: "scale(1.05)" },
                                "100%": { transform: "scale(1)" },
                            },
                        }}
                    >
                        {beingServedMessage}
                    </Alert>
                </Slide>

                {/* Demo Mode Indicator */}
                <Typography
                    variant="body2"
                    color="primary"
                    fontWeight="bold"
                    mt={1}
                >
                    {isDemoMode ? "DEMO MODE - Ingen faktisk kø generert" : ""}
                </Typography>

                {/* Optional: Show selected services */}
                {selectedOptions.length > 0 && (
                    <Card
                        sx={{
                            mt: 2,
                            mb: 2,
                            maxWidth: 600,
                            width: "100%",
                            boxShadow: 2,
                            borderRadius: 2,
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                textAlign="center"
                                mb={3}
                                color="primary"
                            >
                                {selectedServicesLabel || "Your selected services:"}
                            </Typography>
                            <Box
                                display="grid"
                                gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))"
                                gap={2}
                                justifyContent="center"
                            >
                                {selectedOptions.map((service, index) => (
                                    <Card
                                        key={index}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "grey.300",
                                            borderRadius: 2,
                                            boxShadow: "none",
                                            backgroundColor: "white",
                                            transition: "all 0.2s ease-in-out",
                                            "&:hover": {
                                                boxShadow: 1,
                                                transform: "translateY(-2px)",
                                            },
                                        }}
                                    >
                                        <CardContent
                                            sx={{
                                                padding: 2,
                                                textAlign: "center",
                                            }}
                                        >
                                            <Typography
                                                variant="h6"
                                                fontWeight="bold"
                                                color="text.primary"
                                                sx={{
                                                    fontSize: "1.1rem",
                                                    marginBottom: 1,
                                                }}
                                            >
                                                {service.service_name ||
                                                    service}
                                            </Typography>
                                            {service.service_tag && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{
                                                        fontSize: "0.85rem",
                                                        lineHeight: 1.4,
                                                    }}
                                                >
                                                    {service.service_tag}
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Waiting Time Message (branch text, hidden when unset) */}
                {waitingTimeMessage && (
                    <Typography variant="body1" mt={1} textAlign="center">
                        {waitingTimeMessage}
                    </Typography>
                )}

                {/* Service Recommendation (branch text, hidden when unset) */}
                {serviceRecommendationText && (
                    <Typography
                        variant="body2"
                        mt={1}
                        textAlign="center"
                        maxWidth={500}
                    >
                        {serviceRecommendationText}
                    </Typography>
                )}

                {/* Rotating Banner (hidden entirely when the branch has no ads) */}
                {(loading || error || ads.length > 0 || isDemoMode) && (
                <Box
                    mt={3}
                    sx={{
                        width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
                        maxWidth: { xs: "100%", sm: 600, md: 700, lg: 800 },
                        margin: "0 auto",
                        px: { xs: 1, sm: 2 }, // Add horizontal padding for mobile
                    }}
                >
                    {loading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                minHeight: {
                                    xs: "200px",
                                    sm: "250px",
                                    md: "300px",
                                    lg: "350px",
                                },
                            }}
                        >
                            <CircularProgress
                                size={{ xs: 40, sm: 50, md: 60 }}
                            />
                        </Box>
                    ) : error ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                minHeight: {
                                    xs: "200px",
                                    sm: "250px",
                                    md: "300px",
                                    lg: "350px",
                                },
                                backgroundColor: "#f5f5f5",
                                borderRadius: {
                                    xs: "4px",
                                    sm: "6px",
                                    md: "8px",
                                },
                                border: "2px dashed #ccc",
                            }}
                        >
                            <Typography
                                color="error"
                                variant="body2"
                                sx={{
                                    textAlign: "center",
                                    px: 2,
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                }}
                            >
                                {error}
                            </Typography>
                        </Box>
                    ) : ads.length > 0 ? (
                        <Box
                            sx={{
                                width: "100%",
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                maxHeight: {
                                    xs: "300px",
                                    sm: "350px",
                                    md: "400px",
                                    lg: "450px",
                                },
                                borderRadius: {
                                    xs: "4px",
                                    sm: "6px",
                                    md: "8px",
                                },
                                overflow: "hidden",
                                position: "relative",
                                backgroundColor: "#f8f9fa", // Light background for image container
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <img
                                src={ads[currentIndex]?.image_url}
                                alt={
                                    ads[currentIndex]?.title ||
                                    `Portrait Ad ${currentIndex + 1}`
                                }
                                // target_url has been editable in ad management
                                // all along but was never read anywhere, so
                                // every link a client entered was discarded.
                                onClick={
                                    ads[currentIndex]?.target_url
                                        ? () => handleAdClick(ads[currentIndex])
                                        : undefined
                                }
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    transition: "opacity 1s ease-in-out",
                                    display: "block",
                                    cursor: ads[currentIndex]?.target_url
                                        ? "pointer"
                                        : "default",
                                }}
                            />
                        </Box>
                    ) : isDemoMode ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                height: {
                                    xs: "250px",
                                    sm: "300px",
                                    md: "350px",
                                    lg: "400px",
                                },
                                minHeight: {
                                    xs: "200px",
                                    sm: "250px",
                                    md: "300px",
                                    lg: "350px",
                                },
                                backgroundColor: "#f5f5f5",
                                borderRadius: {
                                    xs: "4px",
                                    sm: "6px",
                                    md: "8px",
                                },
                                border: "2px dashed #ccc",
                            }}
                        >
                            <Typography
                                color="text.secondary"
                                variant="body2"
                                sx={{
                                    textAlign: "center",
                                    px: 2,
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                }}
                            >
                                Demo Mode - Loading Ads...
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
                )}

                {/* Footer */}
                <Typography variant="body2" color="textSecondary" mt={3}>
                    easyQue (c) by Digifront
                </Typography>
                <Typography variant="body2" color="primary">
                    sales@digifront.biz
                </Typography>
            </Box>
            <ScrollButtons />
        </>
    );
};

export default QueueDisplay;
